/**
 * Schema Generator Utility
 * Automatically generates JSON-LD structured data for SEO
 * Now supports dynamic templates from database
 */

import { supabase } from "@/integrations/supabase/client";
import Mustache from "mustache";

interface BreadcrumbItem {
  name: string;
  url: string;
}

// Cache for schema templates
let schemaTemplateCache: Map<string, any> | null = null;

// Fetch schema templates from database
const fetchSchemaTemplates = async () => {
  if (schemaTemplateCache) {
    return schemaTemplateCache;
  }

  try {
    const { data, error } = await supabase
      .from('schema_settings')
      .select('schema_type, template, is_enabled')
      .eq('is_enabled', true);

    if (error) throw error;

    schemaTemplateCache = new Map();
    data?.forEach(schema => {
      schemaTemplateCache!.set(schema.schema_type, schema.template);
    });

    return schemaTemplateCache;
  } catch (error) {
    console.error('Error fetching schema templates:', error);
    return new Map();
  }
};

// Helper to render template with data
const renderTemplate = (template: any, data: any): any => {
  try {
    // Process data to avoid double-stringifying arrays
    const processedData = { ...data };
    Object.keys(processedData).forEach(key => {
      if (!Array.isArray(processedData[key]) && typeof processedData[key] === 'object') {
        processedData[key] = JSON.stringify(processedData[key]);
      }
    });
    
    const templateString = JSON.stringify(template);
    const rendered = Mustache.render(templateString, processedData);
    return JSON.parse(rendered);
  } catch (error) {
    console.error('Error rendering template:', error);
    return template;
  }
};


export const generateBreadcrumbSchema = async (items: BreadcrumbItem[]) => {
  const templates = await fetchSchemaTemplates();
  const template = templates.get('breadcrumb');

  if (template) {
    const itemsArray = items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }));
    return renderTemplate(template, { items: itemsArray });
  }

  // Fallback to hardcoded schema
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

interface FAQ {
  question: string;
  answer: string;
}

export const generateFAQSchema = async (faqs: FAQ[]) => {
  if (!faqs || faqs.length === 0) return null;
  
  const templates = await fetchSchemaTemplates();
  const template = templates.get('faq');

  if (template) {
    const faqsArray = faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }));
    return renderTemplate(template, { faqs: JSON.stringify(faqsArray) });
  }

  // Fallback to hardcoded schema
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

export const generateSearchActionSchema = (domain: string) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": domain,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${domain}?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
};

// Generate Organization schema for homepage
export const generateOrganizationSchema = (
  siteName: string,
  domain: string,
  logoUrl: string,
  description?: string,
  socialLinks?: string[]
) => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": siteName,
    "url": domain,
    "logo": logoUrl,
    ...(description && { "description": description }),
    ...(socialLinks && socialLinks.length > 0 && { "sameAs": socialLinks })
  };
};

// Generate ItemList schema for card listings
export const generateItemListSchema = (
  cards: any[],
  domain: string,
  listName: string = "Kreditkarten",
  currency: string = "EUR"
) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": listName,
    "numberOfItems": cards.length,
    "itemListElement": cards.slice(0, 10).map((card, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "CreditCard",
        "@id": `${domain}/kreditkarten/${card.slug}`,
        "name": card.display_name || card.name,
        "description": card.excerpt || `${card.display_name || card.name} von ${card.issuer}`,
        "url": `${domain}/kreditkarten/${card.slug}`,
        "provider": {
          "@type": "Organization",
          "name": card.issuer
        }
      }
    }))
  };
};

// Generate WebPage wrapper schema
export const generateWebPageSchema = (
  url: string,
  name: string,
  description: string,
  mainEntity: any,
  breadcrumb?: any
) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url,
    "name": name,
    "description": description,
    "url": url,
    "mainEntity": mainEntity,
    ...(breadcrumb && { "breadcrumb": breadcrumb })
  };
};

// Generate CollectionPage schema for listings
export const generateCollectionPageSchema = (
  url: string,
  name: string,
  description: string,
  mainEntity: any,
  breadcrumb?: any
) => {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": url,
    "name": name,
    "description": description,
    "url": url,
    "mainEntity": mainEntity,
    ...(breadcrumb && { "breadcrumb": breadcrumb })
  };
};

// Generate Homepage schema with multiple content types
export const generateHomepageSchema = (
  cards: any[],
  articles: any[],
  domain: string,
  siteName: string,
  cardListName: string = "Deutsche Kreditkarten",
  cardListDescription: string = "Vergleichen Sie die besten Kreditkarten in Deutschland",
  articleListName: string = "Artikel über Kreditkarten",
  articleListDescription: string = "Leitfäden und Tipps zu Kreditkarten",
  currency: string = "EUR"
) => {
  const cardList = {
    "@type": "ItemList",
    "name": cardListName,
    "description": cardListDescription,
    "numberOfItems": cards.length,
    "itemListElement": cards.slice(0, 20).map((card, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "CreditCard",
        "@id": `${domain}/kreditkarten/${card.slug}`,
        "name": card.display_name || card.name,
        "description": card.excerpt || `${card.display_name || card.name} von ${card.issuer}`,
        "url": `${domain}/kreditkarten/${card.slug}`,
        "provider": {
          "@type": "Organization",
          "name": card.issuer
        },
        "annualPercentageRate": card.apr_effective,
        "feesAndCommissionsSpecification": card.annual_fee
      }
    }))
  };

  const articleList = articles.length > 0 ? {
    "@type": "ItemList",
    "name": articleListName,
    "description": articleListDescription,
    "numberOfItems": articles.length,
    "itemListElement": articles.slice(0, 10).map((article, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Article",
        "@id": `${domain}/ratgeber/${article.slug}`,
        "headline": article.title,
        "description": article.excerpt || article.title,
        "url": `${domain}/ratgeber/${article.slug}`,
        ...(article.featured_image_url && { "image": article.featured_image_url }),
        ...(article.published_at && { "datePublished": article.published_at })
      }
    }))
  } : null;

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": siteName,
    "url": domain,
    "mainEntity": articleList ? [cardList, articleList] : cardList
  };
};

// Generate SiteNavigationElement schema
export const generateSiteNavigationSchema = (domain: string) => {
  return {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    "name": "Main Navigation",
    "url": [
      domain,
      `${domain}/ratgeber`
    ]
  };
};

interface CardData {
  id?: string;
  slug: string;
  name: string;
  display_name?: string;
  issuer: string;
  network: string;
  annual_fee: number;
  apr_effective: number;
  apr_nominal?: number;
  logo_url: string | null;
  excerpt?: string | null;
  description?: string | null;
  apply_url: string;
  credit_limit_min?: number;
  credit_limit_max?: number;
  interest_free_days?: number;
  age_min?: number;
  income_min?: number;
  apple_pay?: boolean;
  google_pay?: boolean;
  travel_insurance?: boolean;
  perks?: any;
  benefits?: string[];
  pros?: string[];
  drawbacks?: string[];
  cons?: string[];
  best_for?: string[];
  selling_points?: string[];
  affiliate_link?: string | null;
  terms_url?: string | null;
  updated_at?: string;
}

// Helper to format features array for schema (German only)
const formatFeatures = (card: CardData): string[] => {
  const features: string[] = [];
  if (card.apple_pay) features.push("Apple Pay");
  if (card.google_pay) features.push("Google Pay");
  if (card.travel_insurance) {
    features.push("Reiseversicherung");
  }
  if (card.interest_free_days && card.interest_free_days > 0) {
    features.push(`${card.interest_free_days} Tage zinsfreie Periode`);
  }
  // Add perks
  if (card.perks && Array.isArray(card.perks)) {
    card.perks.forEach((perk: any) => {
      const perkText = typeof perk === 'string' ? perk : (perk.title || perk.name || perk.description);
      if (perkText) features.push(perkText);
    });
  }
  return features.slice(0, 10); // Limit to top 10 features
};

// Helper to generate eligibility requirements text (German only)
const generateEligibilityText = (card: CardData, currency: string = 'EUR'): string => {
  const requirements: string[] = [];
  const currencySymbol = currency === 'EUR' ? '€' : currency;
  
  if (card.age_min) {
    requirements.push(`Mindestalter: ${card.age_min} Jahre`);
  }
  if (card.income_min) {
    requirements.push(`Mindesteinkommen: ${card.income_min.toLocaleString('de-DE')} ${currencySymbol}`);
  }
  if (card.credit_limit_min) {
    requirements.push(`Kreditlimit ab: ${card.credit_limit_min.toLocaleString('de-DE')} ${currencySymbol}`);
  }
  return requirements.join(", ");
};

export const generateEnhancedCardSchema = async (
  card: CardData, 
  domain: string,
  currency: string = 'EUR'
) => {
  const templates = await fetchSchemaTemplates();
  const template = templates.get('card');

  const features = formatFeatures(card);
  const eligibility = generateEligibilityText(card, currency);
  
  const cardData = {
    domain,
    slug: card.slug,
    displayName: card.display_name || card.name,
    description: card.excerpt || card.description || `${card.display_name || card.name} von ${card.issuer}`,
    network: card.network,
    issuer: card.issuer,
    annualFee: card.annual_fee,
    aprEffective: card.apr_effective,
    aprNominal: card.apr_nominal,
    logoUrl: card.logo_url,
    applyUrl: card.affiliate_link || card.apply_url,
    termsUrl: card.terms_url,
    creditLimitMin: card.credit_limit_min,
    creditLimitMax: card.credit_limit_max,
    interestFreeDays: card.interest_free_days,
    ageMin: card.age_min,
    incomeMin: card.income_min,
    applePay: card.apple_pay,
    googlePay: card.google_pay,
    travelInsurance: card.travel_insurance,
    features: JSON.stringify(features),
    eligibility,
    benefits: card.benefits || card.pros,
    bestFor: card.best_for,
    updatedAt: card.updated_at
  };

  if (template) {
    return renderTemplate(template, cardData);
  }

  // Log schema generation in development
  if (import.meta.env.DEV) {
    console.log('[Schema Debug] Generating CreditCard schema for:', card.slug);
  }

  // Enhanced fallback schema with complete data
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "CreditCard",
    "@id": `${domain}/kreditkarten/${card.slug}`,
    "name": card.display_name || card.name,
    "description": card.excerpt || card.description || `${card.display_name || card.name} von ${card.issuer}`,
    "brand": {
      "@type": "Brand",
      "name": card.network
    },
    "provider": {
      "@type": "Organization",
      "name": card.issuer
    },
    "feesAndCommissionsSpecification": card.terms_url || `${domain}/kreditkarten/${card.slug}`,
    "annualPercentageRate": card.apr_effective,
    "gracePeriod": card.interest_free_days ? `P${card.interest_free_days}D` : undefined,
    "image": card.logo_url,
      "url": `${domain}/kreditkarten/${card.slug}`,
    "applicationCategory": "CreditCard",
    "offers": {
      "@type": "Offer",
      "url": card.affiliate_link || card.apply_url,
      "availability": "https://schema.org/InStock",
      "price": card.annual_fee,
      "priceCurrency": currency
    }
  };

  // Add optional fields
  if (card.terms_url) {
    schema.termsOfService = card.terms_url;
  }
  
  if (card.updated_at) {
    schema.dateModified = card.updated_at;
  }

  // Add eligibility requirements
  if (eligibility) {
    schema.eligibilityRequirement = eligibility;
  }

  // Add credit limit info
  if (card.credit_limit_max) {
    schema.amount = {
      "@type": "MonetaryAmount",
      "currency": currency,
      "maxValue": card.credit_limit_max,
      ...(card.credit_limit_min && { "minValue": card.credit_limit_min })
    };
  }

  // Add features as additional properties
  if (features.length > 0) {
    schema.additionalProperty = features.map(feature => ({
      "@type": "PropertyValue",
      "name": "Feature",
      "value": feature
    }));
  }

  return schema;
};

// Generate AggregateRating schema
export const generateAggregateRatingSchema = (
  ratingValue: number,
  reviewCount: number,
  bestRating: number = 5,
  worstRating: number = 1
) => {
  return {
    "@type": "AggregateRating",
    "ratingValue": ratingValue,
    "reviewCount": reviewCount,
    "bestRating": bestRating,
    "worstRating": worstRating
  };
};

// Generate Review schema
export const generateReviewSchema = (
  author: string,
  datePublished: string,
  reviewBody: string,
  ratingValue: number
) => {
  return {
    "@type": "Review",
    "author": {
      "@type": "Person",
      "name": author
    },
    "datePublished": datePublished,
    "reviewBody": reviewBody,
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": ratingValue,
      "bestRating": 5
    }
  };
};

// Generate Offer schema for promotions
export const generateOfferSchema = (
  offerTitle: string,
  description: string,
  price: number = 0,
  validFrom?: string,
  validThrough?: string
) => {
  return {
    "@type": "Offer",
    "name": offerTitle,
    "description": description,
    "price": price,
    "priceCurrency": "EUR",
    ...(validFrom && { "validFrom": validFrom }),
    ...(validThrough && { "validThrough": validThrough }),
    "availability": "https://schema.org/InStock"
  };
};

// Generate HowTo schema for tutorial articles
export const generateHowToSchema = (
  name: string,
  description: string,
  steps: Array<{ stepNumber: number; text: string; image?: string }>,
  totalTime?: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    ...(totalTime && { "totalTime": totalTime }),
    "step": steps.map(step => ({
      "@type": "HowToStep",
      "position": step.stepNumber,
      "text": step.text,
      ...(step.image && { "image": step.image })
    }))
  };
};

interface ArticleData {
  slug: string;
  title: string;
  meta_description?: string;
  featured_image?: string | null;
  published_at?: string;
  updated_at?: string;
  author?: string;
  body?: string;
  category?: {
    name: string;
    slug: string;
  };
  reading_time_minutes?: number;
  view_count?: number;
}

export const generateEnhancedArticleSchema = async (
  article: ArticleData, 
  domain: string,
  siteName: string,
  siteLogoUrl: string,
  defaultAuthor: string
) => {
  const templates = await fetchSchemaTemplates();
  const template = templates.get('article');

  // Strip HTML tags from body for articleBody
  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const plainText = article.body ? stripHtml(article.body) : '';
  const wordCount = plainText.split(/\s+/).filter(Boolean).length;

  const articleData = {
    title: article.title,
    metaDescription: article.meta_description || article.title,
    featuredImage: article.featured_image,
    publishedAt: article.published_at,
    updatedAt: article.updated_at || article.published_at,
    author: article.author || defaultAuthor,
    siteName,
    siteLogoUrl,
    domain,
    slug: article.slug,
    articleBody: plainText.substring(0, 5000),
    categoryName: article.category?.name,
    categorySlug: article.category?.slug,
    readingTimeMinutes: article.reading_time_minutes,
    viewCount: article.view_count,
    wordCount
  };

  if (template) {
    return renderTemplate(template, articleData);
  }

  // Enhanced fallback schema
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description || article.title,
    "image": article.featured_image,
    "datePublished": article.published_at,
    "dateModified": article.updated_at || article.published_at,
    "author": {
      "@type": "Person",
      "name": article.author || defaultAuthor
    },
    "publisher": {
      "@type": "Organization",
      "name": siteName,
      "logo": {
        "@type": "ImageObject",
        "url": siteLogoUrl
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${domain}/ratgeber/${article.slug}`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Startseite",
            "item": domain
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Ratgeber",
            "item": `${domain}/ratgeber`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": article.title
          }
        ]
      }
    }
  };

  // Add optional fields
  if (plainText) {
    schema.articleBody = plainText.substring(0, 5000);
  }

  if (wordCount > 0) {
    schema.wordCount = wordCount;
  }

  if (article.category?.name) {
    schema.articleSection = article.category.name;
  }

  if (article.reading_time_minutes) {
    schema.timeRequired = `PT${article.reading_time_minutes}M`;
  }

  if (article.view_count) {
    schema.interactionStatistic = {
      "@type": "InteractionCounter",
      "interactionType": "https://schema.org/ReadAction",
      "userInteractionCount": article.view_count
    };
  }

  // Add speakable property for voice search optimization
  schema.speakable = {
    "@type": "SpeakableSpecification",
    "cssSelector": [".article-body", ".article-content"]
  };

  // Log schema generation in development
  if (import.meta.env.DEV) {
    console.log('[Schema Debug] Generating Article schema for:', article.slug);
  }

  return schema;
};
